import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import LeaveWorkflowEditor from "./LeaveWorkflowEditor";
import {
  ATTENDANCE_ACTIVITY_KEYS,
  ATTENDANCE_ACTIVITY_LABELS,
  normalizeActivityWorkflows,
} from "../../../../lib/attendanceActivityWorkflows";

/**
 * Per-activity workflows for Attendance Policy → Step 4 (Workflow).
 */
export default function ActivityWorkflowsPolicySection({
  darkMode,
  activityWorkflows,
  onChange,
}) {
  const workflows = normalizeActivityWorkflows(activityWorkflows);
  const [openKey, setOpenKey] = useState("regularise");
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    checkScroll();
    const t = setTimeout(checkScroll, 100);
    window.addEventListener("resize", checkScroll);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", checkScroll);
    };
  }, [checkScroll]);

  const handleWheel = (e) => {
    if (scrollContainerRef.current && e.deltaY) {
      scrollContainerRef.current.scrollLeft += e.deltaY;
      checkScroll();
    }
  };

  const scrollByAmount = (amount) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: amount, behavior: "smooth" });
      setTimeout(checkScroll, 200);
    }
  };

  return (
    <div
      className={`mb-4 rounded-xl border p-4 ${
        darkMode
          ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/30"
          : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/20"
      }`}
    >
      <div className="mb-3 flex items-center gap-1.5">
        <h4 className="text-sm font-semibold text-[hsl(var(--ds-fg))]">Activity workflows</h4>
        <span
          title="Separate approval and cancellation chain per request type. Leave stays under Leave Management; Change Shift / Change WO stay under Shift / Weekly Off."
          className="inline-flex h-3.5 w-3.5 cursor-help items-center justify-center text-[hsl(var(--ds-muted-fg))]"
          aria-label="Activity workflows help"
        >
          ⓘ
        </span>
      </div>
      <div className="relative mb-3 flex items-center gap-1">
        {canScrollLeft && (
          <button
            type="button"
            onClick={() => scrollByAmount(-180)}
            aria-label="Scroll tabs left"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] shadow-sm transition hover:bg-[hsl(var(--ds-muted))]"
          >
            <ChevronLeft size={16} />
          </button>
        )}

        <div
          ref={scrollContainerRef}
          onScroll={checkScroll}
          onWheel={handleWheel}
          className="flex flex-1 items-center gap-1.5 overflow-x-auto pb-2 pt-0.5 scroll-smooth [scrollbar-width:thin] [scrollbar-color:hsl(var(--ds-border))_transparent] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-black/5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[hsl(var(--ds-muted-fg))/0.4] hover:[&::-webkit-scrollbar-thumb]:bg-[hsl(var(--ds-muted-fg))/0.7]"
        >
          {ATTENDANCE_ACTIVITY_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setOpenKey(key)}
              className={`shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition ${
                openKey === key
                  ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                  : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]"
              }`}
            >
              {ATTENDANCE_ACTIVITY_LABELS[key]}
            </button>
          ))}
        </div>

        {canScrollRight && (
          <button
            type="button"
            onClick={() => scrollByAmount(180)}
            aria-label="Scroll tabs right"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] shadow-sm transition hover:bg-[hsl(var(--ds-muted))]"
          >
            <ChevronRight size={16} />
          </button>
        )}
      </div>
      <LeaveWorkflowEditor
        darkMode={darkMode}
        showTitle={false}
        showCancellationTab={openKey !== "attendanceFinalization"}
        isAttendanceFinalization={openKey === "attendanceFinalization"}
        layout="inline"
        reminderLabel="Reminder days"
        workflow={workflows[openKey]}
        onChange={(next) =>
          onChange({
            ...workflows,
            [openKey]: next,
          })
        }
      />
    </div>
  );
}
